import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { LocationsSettings } from "./LocationsSettings";
import { PaymentSettings } from "./PaymentSettings";
import { SiteDetails } from "./SiteDetails";
import {
  useGetDmsSetupsByDmsDealerIdMutation,
  useRemoveDealerSetupMutation,
} from "../../redux/reducer/features/Integration/DmsSetupSlice";
import { AppState } from "../../redux/reducer/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "@reduxjs/toolkit";
import {
  RegenerateTokenApi,
  setSetupId,
  SwitchSetupApi,
} from "../../redux/reducer/features/Auth/authSlice";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PageLoader from "../app/PageLoader";
import { SetupSelectorDropdown } from "../common/SetupSelectorDropdown";
import { SetupType } from "../../models/features/Integration/DmsSetup";
import { setupTypeLabelMap } from "../../services/utilities/utility";

export const WebstoreSettings: React.FunctionComponent = () => {
  const dispatch = useDispatch<Dispatch<any>>();
  const navigate = useNavigate();

  const { targetWebAccessToken, dmsDealerId, dmsSetup, setupId, userId } =
    useSelector((state: AppState) => state.authReducer);

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteFinalConfirm, setDeleteFinalConfirm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const [getDmsSetupsMutation, getDmsSetupsMutationResult] =
    useGetDmsSetupsByDmsDealerIdMutation();
  const [removeSetupMutation, removeSetupMutationResult] =
    useRemoveDealerSetupMutation();

  const switchToNextAvailableSetup = async () => {
    const setupsRes: any = await getDmsSetupsMutation({
      dmsDealerId: dmsDealerId ?? "",
    });
    const setups = setupsRes.data?.entityList ?? [];
    if (setups && setups.length > 0) {
      const nextSetupId = setups[0].id;
      dispatch(
        SwitchSetupApi(
          {
            dmsSetupId: nextSetupId,
            accessToken: targetWebAccessToken ?? "",
            userId: userId ?? "",
          },
          () => {
            window.location.reload();
          },
          () => {},
        ),
      );
    } else {
      dispatch(
        RegenerateTokenApi(
          { dmsSetupId: "", accessToken: targetWebAccessToken ?? "" },
          () => {
            dispatch(setSetupId(""));
          },
        ),
      );
    }
  };

  const onDeleteSetup = () => {
    setDeleteConfirm(false);
    setDeleteFinalConfirm(false);
    removeSetupMutation({ setupId: setupId ?? "" })
      .then(async (res: any) => {
        if (res.data && res.data.status === 0) {
          setTimeout(async () => {
            await switchToNextAvailableSetup();
          }, 100);
        }
      })
      .catch((err) => {});
  };

  const handleOpenFinalConfirm = () => {
    setDeleteConfirm(false);
    setDeleteFinalConfirm(true);
  };

  const handleClose = () => {
    setDeleteConfirm(false);
    setDeleteFinalConfirm(false);
  };

  return (
    <>
      <PageLoader
        isLoading={
          removeSetupMutationResult.isLoading ||
          getDmsSetupsMutationResult.isLoading
        }
      ></PageLoader>
      <Container maxWidth={false}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={"center"}
          mb={2}
        >
          {dmsSetup?.dealerSettings?.hasMultipleSite && (
            <SetupSelectorDropdown />
          )}
          <Button
            sx={{ ml: "auto" }}
            size="large"
            variant="contained"
            onClick={() => {
              if (dmsSetup?.dealerSettings?.hasMultipleSite) {
                navigate("/addSite");
              } else {
                setShowInfo(true);
              }
            }}
            startIcon={<AddIcon />}
          >
            Add site
          </Button>
        </Box>

        <SiteDetails />
        <LocationsSettings />
        <PaymentSettings />

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Chip
            sx={{ fontWeight: "normal" }}
            label="Remove Setup"
            size="small"
            color="error"
            icon={<DeleteForeverIcon />}
            onClick={() => {
              setDeleteConfirm(true);
            }}
          ></Chip>
        </Box>
      </Container>
      <Dialog
        open={deleteConfirm}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          Confirm to Remove
        </DialogTitle>
        <DialogContent>
          WARNING! Performing this action will delete all data from{" "}
          {dmsSetup?.setupType === SetupType.HiImpact
            ? " Target-Web"
            : setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.Shopify]}
          . You will lose all of your data and this CANNOT be undone!
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            autoFocus
            onClick={() => {
              setDeleteConfirm(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleOpenFinalConfirm}>
            Are you sure?
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteFinalConfirm} fullWidth onClose={handleClose}>
        <DialogTitle style={{ cursor: "move" }}>Confirm to Remove</DialogTitle>
        <DialogContent>Are you sure to perform this action?</DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            autoFocus
            onClick={() => {
              setDeleteFinalConfirm(false);
            }}
          >
            NO
          </Button>
          <Button variant="contained" onClick={onDeleteSetup}>
            YES
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showInfo} onClose={() => setShowInfo(false)}>
        <DialogTitle>Multiple Website Syncing</DialogTitle>
        <DialogContent>
          Please contact the sales team to enable the multiple website syncing
          feature.
        </DialogContent>{" "}
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setShowInfo(false)}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
